import type { de } from "./de.ts";

export const fr: typeof de = {
  app: { name: "divespots.ch", srTitle: "divespots.ch : sites de plongée en Suisse" },
  common: { close: "Fermer", loading: "Chargement …", retry: "Réessayer" },
  diver: {
    title: "Compte",
    nav: "Navigation du compte",
    nav_account: "Mon compte",
    nav_suggestions: "Mes suggestions",
    signIn: "Se connecter",
    signOut: "Se déconnecter",
    menu: "Compte de {{name}}",
    signOutFailed: "La déconnexion a échoué. Veuillez réessayer.",
    staffDirectTitle: "Modifier directement",
    signOutOffline: "Se déconnecter nécessite une connexion.",
    staffDirect: "En tant que modérateur, vous modifiez directement.",
    staffDirectLink: "Vers l'espace d'administration",
    needsConnection: "Cette page nécessite une connexion.",
    login: {
      title: "Se connecter",
      lead: "Nous t’envoyons un lien par e-mail. Une clé d’accès ou un mot de passe fonctionnent aussi.",
      registerTitle: "Créer un compte",
      registerLead: "Indique ton adresse e-mail. Nous t’envoyons un lien qui crée ton compte.",
      registerSend: "Créer le compte",
      toRegister: "Pas encore de compte ? S’inscrire",
      toSignIn: "Déjà un compte ? Se connecter",
      email: "E-mail",
      name: "Nom affiché",
      nameHint:
        "Seulement pour un nouveau compte, par exemple ton prénom. Tu pourras le changer plus tard.",
      send: "Envoyer le lien par e-mail",
      passkey: "Se connecter avec une clé d’accès",
      sentTitle: "Regarde dans ta boîte mail",
      sentText:
        "Nous avons envoyé un lien à {{email}}. Il est valable 5 minutes. Ouvre-le sur cet appareil.",
      devHint: "Développement : le lien s’affiche dans la console de l’API.",
      otherEmail: "Utiliser une autre adresse",
      badEmail: "Saisis une adresse e-mail valide.",
      banned: "Compte suspendu.",
      password: "Se connecter avec un mot de passe",
      passwordLabel: "Mot de passe",
      passwordSubmit: "Se connecter",
      wrongCredentials: "E-mail ou mot de passe incorrect",
      passwordMissing: "Veuillez saisir votre e-mail et votre mot de passe.",
      registrationClosed:
        "Les nouvelles inscriptions sont actuellement fermées. Les comptes existants peuvent toujours se connecter.",
      rateLimited: "Trop de tentatives. Patiente un instant.",
      network: "Pas de connexion. Réessaie.",
      failed: "Ça n’a pas fonctionné. Le lien a peut-être expiré ou n’est pas valide.",
    },
    account: {
      title: "Mon compte",
      profile: "Profil",
      name: "Nom affiché",
      nameHint: "Affiché avec tes suggestions.",
      nameInvalid: "Saisis entre 1 et 120 caractères.",
      passkeys: "Clés d’accès",
      passkeyLead:
        "Avec une clé d’accès, tu te connectes sans lien, par empreinte, visage ou code de l’appareil.",
      passkeySetup: "Configurer une clé d’accès",
      passkeyNone: "Aucune clé d’accès pour l’instant.",
      passkeyUnnamed: "Clé d’accès",
      passkeyRemove: "Supprimer la clé d’accès {{name}}",
      passkeyAdded: "Clé d’accès configurée.",
      passkeyFailed: "La clé d’accès n’a pas pu être configurée.",
      passkeyUnsupported: "Cet appareil ou ce navigateur ne prend pas en charge les clés d’accès.",
      offerTitle: "Configurer une clé d’accès ?",
      offerText: "La prochaine fois, tu te connectes directement avec elle, sans e-mail.",
      later: "Plus tard",
      session: "Session",
      dangerTitle: "Supprimer le compte",
      dangerText:
        "Ton compte et tes données de connexion sont supprimés. Tes suggestions et signalements restent, de façon anonyme.",
      delete: "Supprimer le compte",
      deleteTitle: "Vraiment supprimer ton compte ?",
      deleteBody:
        "Cette action est irréversible. Tes suggestions restent sous « Compte supprimé », celles en attente sont retirées. Tes signalements restent anonymes.",
      deleteConfirm: "Supprimer définitivement",
      deleteStaff:
        "Les comptes de l’équipe ne peuvent pas se supprimer eux-mêmes. Un admin doit d’abord te rétrograder.",
    },
    suggestions: {
      title: "Mes suggestions",
      empty:
        "Tu n’as encore rien suggéré. Ouvre un site de plongée et touche « Suggérer une modification », ou suggère un nouveau site.",
    },
  },
  suggest: {
    lead: "Ta suggestion est examinée par l’équipe avant d’être visible.",
    editLead:
      "Corrige ce qui est faux. Seuls les champs modifiés sont proposés. L’équipe les examine avant qu’ils soient visibles.",
    spotButton: "Suggérer un site de plongée",
    spotTitle: "Suggérer un nouveau site de plongée",
    editSpotButton: "Suggérer une modification",
    editSpotTitle: "Suggérer une modification : {{name}}",
    objectButton: "Suggérer un objet",
    objectTitle: "Suggérer un objet : {{name}}",
    objectHere: "Suggérer un objet ici",
    editObjectButton: "Suggérer une modification d’objet",
    editObjectTitle: "Suggérer une modification d’objet : {{name}}",
    loginHint: "Connecte-toi pour signaler une observation ou faire une suggestion",
    noteTitle: "Note pour l’équipe",
    note: "Note",
    noteHint: "Facultatif : comment le sais-tu ?",
    noteHintEdit: "Obligatoire : qu’as-tu modifié et pourquoi ?",
    send: "Envoyer la suggestion",
    thanks: "Merci ! Ta suggestion attend d’être examinée.",
    err: {
      tooMany: "Trop de suggestions ouvertes. Attends que l’équipe en ait examiné, ou retires-en.",
      gone: "Cela n’existe plus ou n’est pas public.",
      noteRequired: "Saisis une note.",
      nothingChanged: "Tu n’as encore rien modifié.",
    },
    status: {
      pending: "En examen",
      approved: "Acceptée",
      rejected: "Refusée",
      withdrawn: "Retirée",
    },
    kind: {
      spot_create: "Nouveau site de plongée",
      object_create: "Nouvel objet",
      spot_edit: "Modification d’un site",
      object_edit: "Modification d’un objet",
    },
    reason: "Motif",
    withdraw: "Retirer",
    withdrawNamed: "Retirer la suggestion « {{name}} »",
    withdrawTitle: "Retirer cette suggestion ?",
    withdrawBody: "L’équipe ne la verra plus.",
    more: "Charger plus",
    openSpot: "Ouvrir le site",
    openObject: "Ouvrir le site avec l’objet",
  },
  review: {
    title: "Suggestions",
    filterStatus: "Statut",
    filterKind: "Type",
    filterSubmitter: "Soumise par",
    empty: "Aucune suggestion.",
    back: "Retour à la liste",
    pendingCount_one: "{{count}} suggestion ouverte",
    pendingCount_other: "{{count}} suggestions ouvertes",
    stale:
      "Périmée : la cible a changé depuis la suggestion. Vérifie bien la comparaison avant d’accepter.",
    targetGone: "La cible de cette modification n’existe plus.",
    diff: "Comparaison",
    field: "Champ",
    before: "Actuel",
    after: "Suggestion",
    value: "Valeur",
    empty_value: "vide",
    approveTitle: "Accepter",
    publish: "Publier directement (sinon en brouillon)",
    reason: "Motif",
    reasonHint: "Facultatif. Le plongeur le voit.",
    adjust: "Ajuster avant d’accepter",
    force: "Accepter malgré tout, bien que périmée",
    approve: "Accepter",
    rejectTitle: "Refuser",
    rejectHint: "Obligatoire. Le plongeur voit le motif.",
    reject: "Refuser",
    outcome: "Résultat",
    adjustedNote: "Ajustée par l’équipe avant d’être acceptée.",
    err: {
      conflict: "Cela a changé entre-temps (périmée ou déjà traitée). Recharge la page.",
      badPayload: "La suggestion ajustée n’est pas valide.",
      reasonRequired: "Saisis un motif.",
    },
    keys: {
      name: "Nom",
      location: "Point d’entrée",
      parkingLocation: "Parking",
      waterBody: "Plan d’eau",
      locality: "Lieu",
      difficulty: "Difficulté",
      maxDepthM: "Profondeur maximale (m)",
      closedPeriods: "Périodes de fermeture",
      ratings: "Évaluations",
      description: "Description",
      access: "Accès",
      parking: "Parking (texte)",
      entry: "Entrée (texte)",
      hazards: "Dangers",
      restrictions: "Restrictions",
      facilities: "Infrastructure",
      type: "Type",
      depthOverrideM: "Profondeur mesurée",
      spotId: "Site de plongée",
    },
  },
  sighting: {
    none: "Aucun signalement",
    lastSeen: "Vu pour la dernière fois : {{date}}",
    lastMissing: "Dernier signalement d’absence : {{date}}",
    count_one: "{{count}} signalement",
    count_other: "{{count}} signalements",
    seenToday: "Vu aujourd’hui",
    seenOn: "Vu le {{date}}",
    gone: "Plus là",
    seenNamed: "{{name}} : signaler vu aujourd’hui",
    goneNamed: "{{name}} : signaler plus là",
    otherDay: "Un autre jour (facultatif)",
    history: "Historique",
    statusSeen: "vu",
    statusGone: "absent",
    thanks: "Merci pour ton signalement !",
    badDate: "La date n’est pas valide (pas dans le futur, au plus 2 ans en arrière).",
    tooMany: "Trop de signalements. Patiente un instant.",
  },
  offline: {
    badge: "Hors ligne",
    save: "Enregistrer hors ligne",
    saved: "Enregistré hors ligne",
    estimate: "Cartes, fond du lac en 3D et photo aérienne : env. {{size}}.",
    confirm: "Enregistrer",
    cancel: "Annuler",
    saving: "Enregistrement … {{percent}} %",
    progress: "Progression de l’enregistrement",
    remove: "Supprimer",
    removeNamed: "Supprimer {{name}} du stockage hors ligne",
    listTitle: "Enregistré hors ligne",
    empty:
      "Rien d’enregistré pour l’instant. Ouvre un site de plongée et touche « Enregistrer hors ligne ».",
    open: "Ouvrir",
    total_one: "{{count}} site de plongée · {{size}}",
    total_other: "{{count}} sites de plongée · {{size}}",
    unavailable: "Non disponible hors ligne",
    unavailableText:
      "Ce site de plongée n’a pas été enregistré. Avec une connexion, tu peux l’enregistrer pour l’utiliser hors ligne.",
    adminNeedsConnection: "L’administration nécessite une connexion",
    error: {
      quota:
        "Pas assez d’espace de stockage sur cet appareil. Supprime des sites enregistrés et réessaie.",
      network: "Pas de connexion. L’enregistrement a été annulé.",
      failed: "L’enregistrement a échoué. Réessaie.",
    },
  },
  pwa: { updateAvailable: "Nouvelle version disponible", reload: "Recharger" },
  minimap: {
    title: "Carte du lac",
    hide: "Masquer la carte du lac",
    show: "Afficher la carte du lac",
    openSpot: "Aller au site {{name}}",
    object: "Objet {{name}}",
    currentSpot: "{{name}} : site actuel",
  },
  diveAccess: {
    chunkFailed: "Le mode plongée n’a pas pu être chargé.",
    reload: "Recharger",
    loadFailed: "Chargement impossible. Réessaie.",
    button: "Mode plongée (bêta)",
    unavailableTitle: "Indisponible",
    unavailable: "Cette page n’est pas disponible.",
    back: "Retour à la carte",
  },
  course: {
    same: "Même objet",
    title: "Cap",
    from: "Cap depuis {{name}}",
    to: "vers",
    pick: "Choisir la destination.",
    pickPin: "Ou touchez un repère.",
    noOthers: "Aucun autre objet.",
    fromHere: "Cap d'ici …",
    action: "Cap",
    rowFrom: "Calculer le cap depuis {{name}}",
    rowTo: "Cap de {{from}} vers {{name}}",
    asDestination: "Comme destination",
    swap: "Inverser départ et destination",
    close: "Fermer le cap",
    computing: "Calcul …",
    unavailable: "Cap indisponible",
    compass: "Boussole",
    true: "géographique {{deg}}°",
    declination: "déclinaison {{value}}",
    distance: "Distance",
    depth: "Profondeur",
    depthUnknown: "Profondeur inconnue",
    slope: "Oblique",
    back: "Retour",
    east: "E",
    west: "O",
    footnote:
      "Aide à l'orientation d'après les positions enregistrées (± quelques mètres). Ne remplace pas la planification de plongée.",
  },
  menu: {
    open: "Menu",
    title: "Menu",
    close: "Fermer le menu",
  },
  language: { label: "Langue", de: "Deutsch", en: "English", fr: "Français", it: "Italiano" },
  search: {
    label: "Rechercher un site de plongée ou un lac",
    placeholder: "Rechercher un site ou un lac",
    clear: "Effacer la recherche",
    noResults: "Aucun site de plongée trouvé",
    results_one: "{{count}} résultat",
    results_other: "{{count}} résultats",
  },
  lakeFilter: { label: "Filtrer par lac", all: "Tous les lacs" },
  map: {
    controls: "Commandes de la carte",
    label: "Carte des sites de plongée",
    layerLabel: "Fond de carte",
    layerMap: "Carte",
    layerAerial: "Photo aérienne",
    zoomIn: "Zoom avant",
    zoomOut: "Zoom arrière",
    locate: "Ma position",
    locateDenied: "Accès à la position refusé. Autorise-le dans les réglages du navigateur.",
    locateUnavailable: "Position indisponible.",
    locateOutside: "Votre position est en dehors de la carte.",
    spotPin: "Site de plongée {{name}}",
    spotPinClosed: "Site de plongée {{name}}, fermé",
    spotPinPermanent: "Site de plongée {{name}}, fermé définitivement",
    cluster: "{{count}} sites de plongée, touche pour zoomer",
    loadError: "Les sites de plongée n’ont pas pu être chargés.",
    attribution: "© swisstopo",
    scale: "Échelle : {{distance}}",
  },
  wind: {
    toggle: "Vent",
    info: "À propos de la prévision du vent",
    offline: "Le vent nécessite une connexion internet",
    label: "Prévision du vent",
    time: "Heure de la prévision",
    now: "Maintenant",
    model: "Prévision du modèle, niveau du lac",
    run: "Run du modèle {{time}}",
    placeCenter: "Centre de la carte",
    readout: "{{place}} : {{kmh}} km/h de {{dir}}",
    readoutNone: "{{place}} : pas de données",
    readoutCalm: "{{place}} : calme",
    dir: { n: "N", ne: "NE", e: "E", se: "SE", s: "S", sw: "SO", w: "O", nw: "NO" },
    creditShort: "Vent :",
    creditMeteo: "MeteoSwiss via",
    chip: "Modèle",
    creditVia: "Vent : MeteoSwiss ICON-CH via",
    interpolated: "interpolé",
    legend: "Vitesse du vent en km/h",
    loading: "Chargement du vent …",
    unavailable: "Le vent est temporairement indisponible.",
  },
  precip: {
    toggle: "Précipitations (pluie et neige)",
    info: "À propos de la prévision des précipitations",
    offline: "Les précipitations nécessitent une connexion internet",
    infoBoth: "À propos de la prévision du vent et des précipitations",
    label: "Prévision des précipitations",
    labelBoth: "Prévision du vent et des précipitations",
    legend: "Pluie et neige",
    class0: "faible",
    class1: "modérée",
    class2: "forte",
    class3: "très forte",
    hours: "{{from}}–{{to}} h",
    readout: "{{place}} : Précipitations : {{hours}} {{mm}} mm/h",
    readoutDry: "{{place}} : {{hours}} pas de précipitations",
    readoutNone: "{{place}} : Précipitations : pas de données",
    model: "Modèle, pas un radar",
    creditShort: "Précipitations :",
    creditBoth: "Vent/Précipitations :",
    creditVia: "Précipitations : MeteoSwiss ICON-CH via",
    creditViaBoth: "Vent/Précipitations : MeteoSwiss ICON-CH via",
    loading: "Chargement des précipitations …",
    unavailable: "Les précipitations sont temporairement indisponibles.",
  },
  sheet: {
    expandPanel: "Développer la barre latérale",
    collapsePanel: "Réduire la barre latérale",
    grabber: "Modifier la hauteur du panneau",
    detentPeek: "Petit",
    detentMedium: "Moyen",
    detentLarge: "Grand",
    notFoundTitle: "Site de plongée introuvable",
    notFoundText: "Ce site de plongée n’existe pas ou n’est pas public.",
    backToMap: "Retour à la carte",
    loadError: "Le site de plongée n’a pas pu être chargé.",
  },
  spot: {
    place: "{{locality}} · {{waterBody}}",
    view: "Vue",
    view2d: "2D",
    view3d: "3D",
    noBathy: "Pas de données sur le fond du lac",
    closedNowUntil: "Fermé jusqu’au {{date}}",
    closedNowReason: "Fermé : {{reason}} jusqu’au {{date}}",
    closedForGood: "Fermé définitivement",
    closedForGoodSince: "Fermé définitivement depuis le {{date}}",
    closedForGoodReason: "Motif : {{reason}}",
    maxDepthBadge: "Max. {{value}}",
    objectCount_one: "{{count}} objet",
    objectCount_other: "{{count}} objets",
  },
  difficulty: {
    very_easy: "Très facile",
    easy: "Facile",
    medium: "Moyen",
    hard: "Difficile",
    very_hard: "Très difficile",
  },
  facts: {
    title: "Infos",
    difficulty: "Difficulté",
    maxDepth: "Profondeur max.",
    waterBody: "Plan d’eau",
    locality: "Lieu",
    waterLevel: "Niveau du lac",
    waterLevelValue: "{{value}} d’altitude",
    entry: "Mise à l’eau",
    parking: "Parking",
  },
  objects: {
    title: "Objets",
    unnamed: "Objet sans nom",
    outside: "hors de l’extrait — augmente le rayon",
    noData: "Pas de données",
    approx: "env.",
    provenance: "≈ calculé d’après le modèle du fond swissBATHY3D, ±0.5 m",
    type: {
      figure: "Statue",
      toilet: "Toilettes",
      wreck: "Épave",
      vehicle: "Véhicule",
      platform: "Plateforme",
      plaque: "Plaque commémorative",
      other: "Objet",
    },
  },
  sections: {
    description: "Description",
    access: "Accès",
    parking: "Stationnement",
    entry: "Mise à l’eau",
    hazards: "Dangers",
    restrictions: "Restrictions",
    facilities: "Infrastructures",
  },
  admin: {
    brand: "Admin",
    menu: "Espace admin",
    nav: "Navigation admin",
    nav_spots: "Sites de plongée",
    nav_lakes: "Lacs",
    nav_users: "Utilisateurs",
    nav_settings: "Réglages",
    nav_audit: "Journal",
    nav_suggestions: "Suggestions",
    toMap: "Vers la carte",
    edit: "Modifier",
    signOut: "Se déconnecter",
    signedInAs: "Connecté en tant que {{name}}, {{role}}",
    common: {
      save: "Enregistrer",
      saved: "Enregistré.",
      cancel: "Annuler",
      delete: "Supprimer",
      edit: "Modifier",
    },
    role: { admin: "Admin", moderator: "Modérateur", diver: "Plongeur" },
    status: {
      all: "Tous",
      draft: "Brouillon",
      published: "Publié",
      pending: "En attente",
    },
    login: {
      title: "Connexion",
      lead: "Réservé aux modérateurs et aux admins. Les comptes sont créés par un admin.",
      email: "E-mail",
      password: "Mot de passe",
      submit: "Se connecter",
      missing: "Saisis ton e-mail et ton mot de passe.",
      invalid: "E-mail ou mot de passe incorrect.",
      banned: "Ce compte est bloqué.",
      rateLimited: "Trop de tentatives. Réessaie plus tard.",
      failed: "Connexion impossible. Réessaie.",
      fixtures: "La connexion nécessite la vraie API (pas le mode fixtures).",
    },
    forbidden: {
      staffOnly: "Cet espace est réservé à l’équipe.",
      title: "Accès refusé",
      body: "Cette page est réservée aux admins.",
      back: "Vers les sites de plongée",
    },
    discard: {
      title: "Abandonner les modifications ?",
      body: "Tes modifications ne sont pas enregistrées et seront perdues.",
      confirm: "Abandonner",
      keep: "Continuer à modifier",
    },
    err: {
      concurrent: "Modifié entre-temps par quelqu’un d’autre — valeurs actualisées.",
      load: "Les données n’ont pas pu être chargées.",
      save: "Ça n’a pas fonctionné. Réessaie.",
      fix: "Corrige les champs signalés.",
      auth: "Ta session a expiré ou tu n’as pas les droits. Reconnecte-toi.",
      required: "Champ obligatoire.",
      invalid: "Valeur invalide.",
      slug: "Minuscules, chiffres et tirets simples uniquement.",
      slugTaken: "Ce slug est déjà pris.",
      coords: "Saisis une coordonnée valide (degrés décimaux).",
      mmdd: "Format MM-JJ, par exemple 11-15.",
      nameRequired: "Saisis au moins une traduction.",
    },
    i18n: {
      language: "Langue des textes",
      missing: "traduction manquante",
      missingHint: "Disponible en allemand, il manque encore le {{lang}}.",
    },
    spots: {
      title: "Sites de plongée",
      new: "Nouveau site de plongée",
      create: "Créer le site",
      edit: "Modifier {{name}}",
      formLabel: "Formulaire du site de plongée",
      filters: "Filtres",
      search: "Rechercher",
      count_one: "{{count}} site de plongée",
      count_other: "{{count}} sites de plongée",
      drafts_one: "{{count}} brouillon",
      drafts_other: "{{count}} brouillons",
      empty: "Aucun site pour ce filtre.",
      open: "Ouvrir {{name}}",
      notFound: "Site de plongée introuvable",
      backToList: "Retour à la liste",
      viewPublic: "Page publique",
      publish: "Publier",
      unpublish: "Repasser en brouillon",
      delete: "Supprimer le site",
      deleteTitle: "Supprimer ce site de plongée ?",
      deleteBody: "« {{name}} » et ses objets disparaissent de la carte. Impossible d’annuler ici.",
      deleteConfirm: "Supprimer définitivement",
      draftNote: "Ce site est un brouillon et reste invisible sur la carte publique.",
    },
    section: {
      basics: "Informations de base",
      location: "Emplacement",
      texts: "Textes",
      closed: "Périodes de fermeture",
      permanent: "Fermeture définitive",
      ratings: "Évaluations",
      publication: "Publication",
    },
    field: {
      name: "Nom",
      slug: "Slug (facultatif)",
      slugDerived: "Laisse vide : ce sera « {{slug}} ».",
      slugHint: "Fait partie de l’adresse, par exemple /fr/spot/…",
      waterBody: "Plan d’eau",
      locality: "Lieu",
      difficulty: "Difficulté",
      none: "Non précisé",
      maxDepth: "Profondeur maximale (m)",
      status: "Statut",
      entry: "Point de mise à l’eau",
      hasParking: "Indiquer un parking",
      parking: "Parking",
      lake: "Lac : {{name}}",
      noLake: "Aucun lac associé",
      hasBathy: "avec données du fond du lac",
    },
    picker: {
      help: "Touche la carte ou déplace l’épingle. Ou saisis les coordonnées.",
      lat: "Latitude (lat)",
      lon: "Longitude (lon)",
    },
    depth: {
      pick: "Choisis un point pour voir la profondeur.",
      ok: "Profondeur d’après swissBATHY3D : ≈ {{depth}}",
      dry: "Ce point est au-dessus du niveau de l’eau.",
      noData: "Pas de données sur le fond du lac à ce point.",
      noLake: "Aucun lac avec données de fond à ce point.",
      error: "La profondeur n’a pas pu être consultée.",
    },
    permanent: {
      toggle: "Fermé définitivement",
      help: "Pour les sites où la plongée n’est plus possible. Ils restent visibles et sont signalés comme fermés.",
      since: "Depuis (AAAA-MM-JJ, facultatif)",
      reason: "Motif",
      marker: "Fermé définitivement",
    },
    closed: {
      help: "Se répète chaque année. Du et au peuvent chevaucher le nouvel an, par exemple 11-15 au 02-28.",
      none: "Aucune période de fermeture.",
      add: "Ajouter une période",
      period: "Période de fermeture {{n}}",
      from: "Du (MM-JJ)",
      to: "Au (MM-JJ)",
      reason: "Motif",
      remove: "Supprimer la période {{n}}",
    },
    ratings: { clear: "Effacer" },
    objects: {
      title: "Objets",
      add: "Ajouter un objet",
      none: "Pas encore d’objets.",
      afterCreate: "Tu pourras ajouter des objets une fois le site créé.",
      addTitle: "Nouvel objet",
      editTitle: "Modifier l’objet",
      edit: "Modifier l’objet {{name}}",
      delete: "Supprimer l’objet {{name}}",
      deleteTitle: "Supprimer cet objet ?",
      deleteBody: "« {{name}} » sera supprimé.",
      type: "Type",
      name: "Nom",
      description: "Description",
      location: "Position de l’objet",
      override: "Profondeur mesurée (m)",
      overrideHint:
        "Facultatif. Laisse vide pour utiliser la profondeur calculée (affichée avec ≈).",
    },
    lakes: {
      title: "Lacs",
      level: "Niveau du lac",
      levelInput: "Niveau du lac (m d’altitude)",
      bathy: "avec données du fond du lac",
      edit: "Modifier le niveau de {{name}}",
      invalid: "Saisis un nombre entre 0 et 5000.",
      warning: "L’enregistrement recalcule les profondeurs de tous les objets de ce lac.",
    },
    settings: {
      title: "Réglages",
      registration: "Inscription",
      allow: "Autoriser les nouvelles inscriptions",
      max: "Max. de nouveaux comptes par heure",
      maxHint: "0 = aucun nouveau compte. Valable pour tout le monde ensemble, à chaque heure.",
      maxInvalid: "Veuillez saisir un nombre entier de 0 à 1000.",
      newLastHour: "Nouveaux comptes (dernière heure)",
      refusedLastHour: "Inscriptions refusées (dernière heure)",
      noMail: "Aucun envoi d'e-mails n'est configuré — l'inscription ne peut pas être ouverte",
      mailConflict:
        "L'inscription n'a pas pu être ouverte : aucun envoi d'e-mails n'est configuré.",
      save: "Enregistrer",
      saved: "Enregistré.",
      updatedAt: "Dernière modification : {{date}}",
    },
    model: {
      title: "Modèle 3D",
      save: "Enregistrer le modèle",
      none: "Aucun modèle 3D téléversé.",
      choose: "Choisir un fichier",
      replace: "Remplacer le modèle",
      file: "Fichier .glb",
      drop: "Dépose un fichier .glb ici ou choisis-en un (jusqu’à {{max}} Mo).",
      chosen: "{{name}} ({{size}}), pas encore téléversé",
      attribution: "Source / licence",
      attributionHint: "Qui a créé le modèle, sous quelle licence ? Affiché en mode plongée.",
      scale: "Échelle",
      heading: "Orientation (°)",
      headingHint:
        "0 à moins de 360, direction de la face avant, dans le sens horaire depuis le nord.",
      discard: "Abandonner la sélection",
      discardBody:
        "Les modifications du modèle 3D ne sont pas encore enregistrées et seront perdues.",
      headingShort: "Orientation",
      upload: "Téléverser",
      remove: "Supprimer le modèle",
      removeTitle: "Supprimer le modèle ?",
      removeBody: "Le modèle 3D de cet objet est supprimé. L’objet reste.",
      uploaded: "Modèle téléversé.",
      saved: "Enregistré.",
      deleted: "Modèle supprimé.",
      size: "Taille",
      triangles: "triangles",
      bounds: "Dimensions",
      animations: "Animations",
      preview: "Aperçu du modèle 3D",
      previewLoading: "Chargement de l’aperçu …",
      previewFailed: "Aperçu indisponible.",
      north: "N",
      err: {
        extension: "Seuls les fichiers .glb (glTF binaire) sont autorisés.",
        empty: "Le fichier est vide.",
        size: "Le fichier dépasse 8 Mo.",
        magic: "Ce n’est pas un fichier .glb valide.",
        tooLarge: "Le fichier est trop volumineux (8 Mo au maximum).",
        forbidden: "Seuls les admins peuvent modifier les modèles 3D.",
        rateLimited: "Trop d'envois. Veuillez patienter un instant.",
        notFound: "Le modèle a été supprimé entre-temps. Veuillez recharger.",
        scale: "Échelle entre 0,01 et 100.",
        heading: "Orientation de 0 à moins de 360.",
      },
    },
    audit: {
      title: "Journal",
      lead: "Qui a modifié quoi, et quand. Réservé aux admins.",
      filterEntity: "Quoi",
      filterAction: "Action",
      all: "Tous",
      when: "Date",
      who: "Qui",
      action: "Action",
      what: "Quoi",
      script: "Script : {{label}}",
      deletedAccount: "Compte supprimé",
      deletedTarget: "supprimé",
      unnamed: "sans nom",
      sugg: "Suggestion",
      details: "Modifications ({{count}})",
      more: "plus",
      less: "moins",
      empty: "Aucune entrée.",
      count_one: "{{count}} entrée",
      count_other: "{{count}} entrées",
      loadMore: "Charger plus",
      filterBy: "Afficher seulement {{name}}",
      removeFilter: "Retirer le filtre : {{name}}",
      chipSpot: "Site : {{name}}",
      chipActor: "Par : {{name}}",
      unknownSpot: "Site de plongée",
      unknownPerson: "Personne",
      history: "Historique",
      yes: "oui",
      no: "non",
      adjusted: "Accepté avec modifications",
      cascaded: "Supprimé avec le site",
      redacted: "Données personnelles effacées",
      reason: "Motif : {{text}}",
      kind: "Type : {{kind}}",
      accountDeleted: "Compte supprimé",
      entity: {
        spot: "Site de plongée",
        object: "Objet",
        suggestion: "Suggestion",
        sighting: "Observation",
        lake: "Lac",
        user: "Utilisateur",
        settings: "Réglages",
      },
      actions: {
        create: "créé",
        update: "modifié",
        delete: "supprimé",
        submit: "suggéré",
        withdraw: "retiré",
        approve: "accepté",
        reject: "refusé",
      },
      field: {
        name: "Nom",
        slug: "Slug",
        location: "Position",
        waterBody: "Plan d'eau",
        locality: "Lieu",
        difficulty: "Difficulté",
        maxDepthM: "Profondeur maximale (m)",
        closedPeriods: "Périodes de fermeture",
        permanentClosure: "Fermeture définitive",
        status: "Statut",
        parkingLocation: "Position du parking",
        description: "Description",
        access: "Accès",
        parking: "Parking",
        entry: "Mise à l'eau",
        hazards: "Dangers",
        restrictions: "Restrictions",
        facilities: "Infrastructure",
        ratings: "Évaluations",
        ratings_fish: "Évaluation poissons",
        ratings_walls: "Évaluation parois",
        ratings_caves: "Évaluation grottes",
        ratings_wrecksSculptures: "Évaluation épaves et sculptures",
        ratings_current: "Évaluation courant",
        ratings_surroundings: "Évaluation environnement",
        type: "Type",
        depthOverrideM: "Profondeur, manuelle (m)",
        model: "Modèle 3D",
        model_scale: "Modèle 3D : échelle",
        model_headingDeg: "Modèle 3D : orientation (°)",
        model_modelId: "Modèle 3D : fichier",
        waterLevelM: "Niveau d'eau (m s. m.)",
        hasBathy: "Avec données du fond",
        registrationOpen: "Inscription ouverte",
        maxNewAccountsPerHour: "Nouveaux comptes par heure (max.)",
        diveAccess: "Mode plongée",
        banned: "Bloqué",
        role: "Rôle",
        password: "Mot de passe",
        present: "Toujours là",
      },
      countMore_one: "{{count}} entrée la plus récente · d’autres disponibles",
      countMore_other: "{{count}} entrées les plus récentes · d’autres disponibles",
      fieldLabel: "{{label}} :",
      activeFilters: "Filtres actifs",
    },
    users: {
      search: "Rechercher un nom ou un e-mail",
      filterRole: "Rôle",
      filterStatus: "Statut",
      all: "Tous",
      active: "Actif",
      blocked: "Bloqué",
      new24h: "Nouveaux des dernières 24 h",
      email: "E-mail",
      registered: "Inscrit",
      lastSeen: "Dernière visite",
      never: "jamais",
      pending: "Suggestions en attente",
      user: "Utilisateur",
      pendingShort: "Ouvert",
      diveModeShort: "incl.",
      diveMode: "Mode plongée",
      diveModeOf: "Mode plongée pour {{name}}",
      diveModeAdmin: "Inclus avec l’accès admin",
      empty: "Aucun utilisateur trouvé.",
      count: "{{count}} utilisateurs",
      banTitle: "Bloquer {{name}} ?",
      banBody: "{{name}} est déconnecté partout immédiatement et ne peut plus se connecter.",
      unbanTitle: "Débloquer {{name}} ?",
      unbanBody: "{{name}} peut de nouveau se connecter.",
      title: "Utilisateurs",
      you: "toi",
      role: "Rôle",
      roleOf: "Rôle de {{name}}",
      ban: "Bloquer",
      unban: "Débloquer",
      banOf: "Bloquer {{name}}",
      unbanOf: "Débloquer {{name}}",
      banned: "Bloqué",
      selfNote: "Ton rôle et ton blocage ne sont pas modifiables.",
      selfConflict: "Ce n’est pas possible pour ton propre compte.",
      create: "Créer un utilisateur",
      createSubmit: "Créer l’utilisateur",
      created: "{{email}} a été créé.",
      name: "Nom",
      initialPassword: "Mot de passe initial",
      passwordHint:
        "12 caractères au minimum. Remets-le en main propre, pas par e-mail ni par chat.",
      passwordShort: "12 caractères au minimum.",
      emailInvalid: "Saisis une adresse e-mail valide.",
      emailTaken: "Cette adresse e-mail est déjà utilisée.",
    },
  },
  three: {
    shoreLeft: "Le long de la rive, à gauche",
    shoreRight: "Le long de la rive, à droite",
    backToEntry: "Retour à l’entrée",
    lakeEnd: "Fin du lac atteinte",
    shoreLost: "Rive introuvable",
    spotPin: "Site de plongée {{name}}",
    canvas:
      "Modèle 3D du fond du lac. Glisse pour tourner, touche pour lire la profondeur. Utilise les flèches pour déplacer un réticule sur le fond du lac.",
    back2d: "Vue 2D",
    radius: "Rayon",
    exaggeration: "Exagération",
    exaggerationValue: "{{value}}×",
    water: "Surface de l’eau",
    planes: "Niveaux de profondeur",
    compassNow: "Boussole, direction du regard {{deg}} degrés {{name}}",
    cardinal: { N: "N", NE: "NE", E: "E", SE: "SE", S: "S", SW: "SO", W: "O", NW: "NO" },
    cardinalName: {
      N: "nord",
      NE: "nord-est",
      E: "est",
      SE: "sud-est",
      S: "sud",
      SW: "sud-ouest",
      W: "ouest",
      NW: "nord-ouest",
    },
    compass: "Boussole, touche pour revenir à la vue initiale",
    loading: "Chargement du fond du lac …",
    noDataHere: "Pas de données sur le fond du lac ici",
    rateLimited: "Trop de requêtes. Réessaie plus tard.",
    loadFailed: "Le fond du lac n’a pas pu être chargé.",
    noWebgl: "Ton navigateur ne peut pas afficher de graphiques 3D (WebGL).",
    contextLost: "Les graphiques 3D ont été interrompus.",
    reload: "Recharger",
    legend: "Profondeur",
    readoutNote: "swissBATHY3D, ±0.5 m",
    readoutNoData: "Pas de données",
    readoutLand: "Rive",
    pinLabel: "Objet {{name}}, {{depth}}",
    place: {
      toggle: "Placer un objet",
      hint: "Touchez le fond du lac pour placer un objet.",
      movingHint: "Touchez le fond du lac : destination de « {{name}} ».",
      cancel: "Annuler",
      move: "Déplacer",
      moveConfirmTitle: "Déplacer « {{name}} » ?",
      moveConfirmBody:
        "« {{name}} » sera déplacé à l’endroit touché. La profondeur est recalculée.",
      moveConfirm: "Déplacer",
      depthPreview: "Profondeur calculée : {{depth}} (aperçu)",
      noDepth: "Pas de données de profondeur – saisissez la profondeur à la main.",
      panel: "Placer un objet",
    },
    objectClose: "Fermer les détails de l’objet",
  },
  ratings: {
    title: "Évaluations",
    fish: "Poissons",
    walls: "Parois",
    caves: "Grottes",
    wrecksSculptures: "Épaves et sculptures",
    current: "Courant",
    surroundings: "Environnement",
    value: "{{label}} : {{value}} sur 5",
  },
};
